/**
 * Video Service
 * Handles all video-related operations with Supabase
 */

import { getSupabaseClient } from '../utils/supabaseClient';
import type { 
  Video, 
  VideoCategory, 
  VideoFormData, 
  VideoSearchParams
} from '../types/video';
import {
  extractYouTubeId,
  fetchYouTubeMetadata,
  extractVideoMetadata
} from './video/youtubeHelpers';
import {
  applyVideoFilters,
  applyVideoSorting,
  applyVideoPagination,
  buildSearchQuery
} from './video/queryHelpers';

// Lazy-load Supabase client to avoid circular dependency
let supabaseInstance: any = null;
const getSupabase = () => {
  if (!supabaseInstance) {
    supabaseInstance = getSupabaseClient();
  }
  return supabaseInstance;
};

/**
 * Add a new video to the database
 */
export const addVideo = async (
  formData: VideoFormData,
  apiKey?: string
): Promise<Video | null> => {
  try {
    const videoId = extractYouTubeId(formData.youtube_url);
    
    if (!videoId) {
      throw new Error('Invalid YouTube URL');
    }

    let videoData: Partial<Video> = {
      youtube_id: videoId,
      url: `https://www.youtube.com/watch?v=${videoId}`,
      title: `Video ${videoId}`, // Default title
      category: formData.category,
      tags: formData.tags,
      notes: formData.notes,
      is_featured: formData.is_featured || false,
      is_active: true,
    };

    // Fetch metadata from YouTube if API key is provided
    if (apiKey) {
      const youtubeData = await fetchYouTubeMetadata(videoId, apiKey);
      
      if (youtubeData) {
        const metadata = extractVideoMetadata(youtubeData, formData.tags);
        videoData = {
          ...videoData,
          ...metadata
        };
      }
    }

    const { data, error } = await getSupabase()
      .from('videos')
      .insert([videoData])
      .select()
      .single();

    if (error) {
      // Error adding video
      return null;
    }

    return data;
  } catch (_error) {
    // Error in addVideo
    return null;
  }
};

/**
 * Get all videos with optional filtering
 */
export const getVideos = async (
  params?: VideoSearchParams
): Promise<Video[]> => {
  try {
    let query = getSupabase()
      .from('videos')
      .select('*')
      .eq('is_active', true);

    // Apply filters, sorting, and pagination
    query = applyVideoFilters(query, params);
    query = applyVideoSorting(query, params?.sort_by);
    query = applyVideoPagination(query, params);

    const { data, error } = await query;

    if (error) {
      // Error fetching videos
      return [];
    }

    return data || [];
  } catch (_error) {
    // Error in getVideos
    return [];
  }
};

/**
 * Get a single video by ID
 */
export const getVideoById = async (id: string): Promise<Video | null> => {
  try {
    const { data, error } = await getSupabase()
      .from('videos')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      // Error fetching video
      return null;
    }

    return data;
  } catch (_error) {
    // Error in getVideoById
    return null;
  }
};

/**
 * Update a video
 */
export const updateVideo = async (
  id: string,
  updates: Partial<Video>
): Promise<Video | null> => {
  try {
    const { data, error } = await getSupabase()
      .from('videos')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      // Error updating video
      return null;
    }

    return data;
  } catch (_error) {
    // Error in updateVideo
    return null;
  }
};

/**
 * Delete a video (soft delete by setting is_active to false)
 */
export const deleteVideo = async (id: string): Promise<boolean> => {
  try {
    const { error } = await getSupabase()
      .from('videos')
      .update({ is_active: false })
      .eq('id', id);

    if (error) {
      // Error deleting video
      return false;
    }

    return true;
  } catch (_error) {
    // Error in deleteVideo
    return false;
  }
};

/**
 * Get all video categories
 */
export const getVideoCategories = async (): Promise<VideoCategory[]> => {
  try {
    const { data, error } = await getSupabase()
      .from('video_categories')
      .select('*')
      .eq('is_active', true)
      .order('order', { ascending: true });

    if (error) {
      // Error fetching categories
      return [];
    }

    return data || [];
  } catch (_error) {
    // Error in getVideoCategories
    return [];
  }
};

/**
 * Search videos by text query
 */
export const searchVideos = async (query: string): Promise<Video[]> => {
  try {
    const searchQuery = buildSearchQuery(query);
    
    const { data, error } = await getSupabase()
      .from('videos')
      .select('*')
      .eq('is_active', true)
      .or(searchQuery)
      .order('added_at', { ascending: false });

    if (error) {
      // Error searching videos
      return [];
    }

    return data || [];
  } catch (_error) {
    // Error in searchVideos
    return [];
  }
};

/**
 * Get featured videos
 */
export const getFeaturedVideos = async (): Promise<Video[]> => {
  try {
    const { data, error } = await getSupabase()
      .from('videos')
      .select('*')
      .eq('is_active', true)
      .eq('is_featured', true)
      .order('added_at', { ascending: false });

    if (error) {
      // Error fetching featured videos
      return [];
    }

    return data || [];
  } catch (_error) {
    // Error in getFeaturedVideos
    return [];
  }
};

// Re-export helpers for backward compatibility
export { extractYouTubeId, fetchYouTubeMetadata } from './video/youtubeHelpers';